import { apiRequest } from "./client";
import type { AcademyRole } from "../types/user";

export type AssignmentTarget = "user" | "role";
export type ProgressStatus = "not_started" | "in_progress" | "completed";

export interface AssignmentOut {
  id: number;
  course_id: number;
  target_type: AssignmentTarget;
  target_user_id: number | null;
  target_role: AcademyRole | null;
  due_at: string | null;
  assigned_by_user_id: number;
  assigned_at: string;
  revoked_at: string | null;
  target_user_login: string | null;
  target_user_full_name: string | null;
}

export interface MyCourseSummary {
  course_id: number;
  course_title: string;
  course_slug: string;
  cover_file_id: string | null;
  is_mandatory: boolean;
  due_at: string | null;
  overdue: boolean;
  lessons_total: number;
  lessons_completed: number;
  percent: number;
}

export interface LessonProgressOut {
  lesson_id: number;
  status: ProgressStatus;
  started_at: string | null;
  completed_at: string | null;
  time_spent_seconds: number;
  checklist_state: Record<string, boolean>;
}

export interface CreateAssignmentPayload {
  target_type: AssignmentTarget;
  target_user_id?: number;
  target_role?: AcademyRole;
  due_at?: string;
}

export function createAssignment(
  courseId: number,
  payload: CreateAssignmentPayload,
): Promise<AssignmentOut> {
  return apiRequest<AssignmentOut>(`/courses/${courseId}/assignments`, {
    method: "POST",
    body: payload,
  });
}

export function listAssignments(courseId: number): Promise<AssignmentOut[]> {
  return apiRequest<AssignmentOut[]>(`/courses/${courseId}/assignments`);
}

export function revokeAssignment(assignmentId: number): Promise<void> {
  return apiRequest<void>(`/assignments/${assignmentId}`, { method: "DELETE" });
}

export function myCourses(): Promise<{ items: MyCourseSummary[] }> {
  return apiRequest<{ items: MyCourseSummary[] }>("/me/courses");
}

export function userCoursesProgress(userId: number): Promise<{ items: MyCourseSummary[] }> {
  return apiRequest<{ items: MyCourseSummary[] }>(`/users/${userId}/courses-progress`);
}

export interface NextLessonOut {
  lesson_id: number;
  lesson_title: string;
  lesson_type: string;
  estimated_minutes: number | null;
  module_id: number;
  module_title: string;
  module_position: number;
  lesson_position: number;
}

export interface StudentDashboardOut {
  current_course: MyCourseSummary | null;
  next_lesson: NextLessonOut | null;
}

export function myDashboard(): Promise<StudentDashboardOut> {
  return apiRequest<StudentDashboardOut>("/me/dashboard");
}

export interface ModuleProgressOut {
  module_id: number;
  module_title: string;
  module_position: number;
  lessons_total: number;
  lessons_completed: number;
  percent: number;
}

export interface LessonProgressInCourseOut {
  lesson_id: number;
  module_id: number;
  status: ProgressStatus;
}

export interface CourseProgressDetailOut {
  course_id: number;
  lessons_total: number;
  lessons_completed: number;
  percent: number;
  modules: ModuleProgressOut[];
  lessons: LessonProgressInCourseOut[];
}

export function myCourseProgress(courseId: number): Promise<CourseProgressDetailOut> {
  return apiRequest<CourseProgressDetailOut>(`/me/courses/${courseId}/progress`);
}

export type LessonTypeStr = "text" | "video" | "file" | "checklist" | "quiz" | "mixed";

export interface ModuleLessonViewOut {
  lesson_id: number;
  position: number;
  title: string;
  lesson_type: LessonTypeStr;
  estimated_minutes: number | null;
  is_required: boolean;
  status: ProgressStatus;
}

export interface ModuleFinalQuizOut {
  quiz_id: number;
  title: string;
  passing_score_percent: number;
  max_attempts: number | null;
  attempts_used: number;
  best_score_percent: number | null;
  passed: boolean;
}

export interface StudentModuleViewOut {
  module_id: number;
  module_title: string;
  module_description: string | null;
  module_position: number;
  course_id: number;
  course_title: string;
  lessons_total: number;
  lessons_completed: number;
  percent: number;
  lessons: ModuleLessonViewOut[];
  final_quiz: ModuleFinalQuizOut | null;
}

export function myModuleView(moduleId: number): Promise<StudentModuleViewOut> {
  return apiRequest<StudentModuleViewOut>(`/me/modules/${moduleId}`);
}

export function lessonProgress(lessonId: number): Promise<LessonProgressOut> {
  return apiRequest<LessonProgressOut>(`/lessons/${lessonId}/progress`);
}

export function completeLesson(
  lessonId: number,
  payload: { time_spent_seconds?: number; checklist_state?: Record<string, boolean> } = {},
): Promise<LessonProgressOut> {
  return apiRequest<LessonProgressOut>(`/lessons/${lessonId}/complete`, {
    method: "POST",
    body: payload,
  });
}
